import React, { useEffect, useState } from 'react'
import { Modal, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import * as displayOptionService from '../services/display-option.service'
import type { DisplayOptionDto, Uom } from '../services/display-option.service'

/**
 * "+ Add display mode" creation dialog — closes the gap flagged in story
 * 2.4's deferred-work.md ("createDisplayMode is implemented and unit-tested
 * in the service layer but never wired to any control"). Field set mirrors
 * OptionDetail's editable display-mode fields (name/dims/uom/capacity);
 * stacking/hanging are left at their backend defaults (off) here and can be
 * turned on afterwards from the detail pane, same as any other option edit.
 *
 * AntD Modal conventions match AffectedInstancesConfirm.tsx (this card's
 * existing dialog): open/onCancel/centered/footer={null} custom footer,
 * styles.header/body/content for dark-mode, style.direction for RTL.
 */
export interface AddDisplayModeDialogProps {
  visible: boolean
  productId: number
  service?: typeof displayOptionService
  onCreated: (created: DisplayOptionDto) => void
  onClose: () => void
  onError: (message: string) => void
  isRTL?: boolean
  dmode?: boolean
}

/**
 * The app's Unit-of-Measure list, COPIED from `uomList` in
 * `_services/common.service.js` rather than imported.
 *
 * Not a preference — that module cannot be loaded here. It imports
 * `_translations/i18n.js` at module scope, which throws "You are passing an
 * undefined module! Please check the object you are passing to i18next.use()"
 * under Jest, taking down every suite that touches it (the same pre-existing
 * fault that already stops `SaveBlocking` and `SummaryView` from running at
 * all). Importing it here failed three card suites outright.
 *
 * Same accepted-duplication pattern this feature already uses for the
 * orientation labels in `export/shared/orientation-column.ts`, and with the
 * same obligation: keep in sync if the app's list ever changes.
 */
const APP_UOM_LIST: Record<string, string> = {
  meters: 'Meters',
  cm: 'Centimeters',
  inches: 'Inches',
  feet: 'Feet',
}

/** Same source as OptionDetail's — the app's own `uomList`, so this picker
 *  offers exactly what every other Unit of Measure picker in Planigo does
 *  (`mm` and `none` are in the backend enum but in no other picker, and in
 *  no planning document). Leading blank matches the sibling picker's "-". */
const UOM_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '-' },
  ...Object.entries(APP_UOM_LIST).map(([value, label]) => ({ value, label })),
]

/** A native number input hands back a string; '' means "cleared". */
const toNumber = (raw: string): number | null => {
  if (raw.trim() === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export type AddModeNumericField = 'width' | 'height' | 'depth' | 'capacity'

/**
 * fix-019 (QA, 2026-10-16): the dialog only checked the name, so 0 (or a
 * blank, or a negative) in Width/Height/Depth/Capacity was saved as-is — a
 * zero-width mode then breaks every width/facing calculation downstream.
 * Revised 2026-10-19 (Ashan): only the name is mandatory. All four numbers
 * are optional and may be 0; only a negative is rejected (capacity must
 * also be a whole number).
 * Returns the i18n key suffix of the problem, or null when the value is fine.
 */
export const numericFieldError = (
  field: AddModeNumericField,
  value: number | null
): 'nonNegative' | 'capacityWhole' | null => {
  if (value == null) return null
  if (field === 'capacity') {
    return Number.isInteger(value) && value >= 0 ? null : 'capacityWhole'
  }
  return value >= 0 ? null : 'nonNegative'
}

export const NUMERIC_ERROR_DEFAULTS = {
  nonNegative: 'Cannot be negative.',
  capacityWhole: 'Must be a whole number, 0 or more.',
}

const EMPTY_FORM = {
  name: '',
  width: null as number | null,
  height: null as number | null,
  depth: null as number | null,
  uom: 'cm' as Uom,
  capacity: null as number | null,
}

export const AddDisplayModeDialog: React.FC<AddDisplayModeDialogProps> = ({
  visible,
  productId,
  service = displayOptionService,
  onCreated,
  onClose,
  onError,
  isRTL,
  dmode,
}) => {
  const { t } = useTranslation()
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  // Fresh form every time the dialog opens — a previous attempt's typed
  // name/dims must not leak into the next "Add display mode" click.
  useEffect(() => {
    if (visible) setForm(EMPTY_FORM)
  }, [visible])

  const nameValid = form.name.trim().length > 0

  const fieldErrors = {
    width: numericFieldError('width', form.width),
    height: numericFieldError('height', form.height),
    depth: numericFieldError('depth', form.depth),
    capacity: numericFieldError('capacity', form.capacity),
  }
  const numbersValid = Object.values(fieldErrors).every((e) => e == null)

  const renderFieldError = (field: AddModeNumericField) => {
    const error = fieldErrors[field]
    if (!error) return null
    return (
      <div
        className="simv3-doc__field-error"
        style={{ color: '#dc3545', fontSize: 12, marginTop: 2 }}
        data-testid={`add-display-mode-${field}-error`}
      >
        {t(`simulationV3.displayOptions.dialog.addMode.errors.${error}`, {
          defaultValue: NUMERIC_ERROR_DEFAULTS[error],
        })}
      </div>
    )
  }

  const handleCreate = async () => {
    if (!nameValid || !numbersValid) return
    setSaving(true)
    try {
      const created = await service.createDisplayMode(productId, {
        type: 'display_mode',
        name: form.name.trim(),
        width: form.width,
        height: form.height,
        depth: form.depth,
        uom: form.uom,
        capacity: form.capacity,
      })
      setSaving(false)
      onCreated(created)
    } catch (err) {
      setSaving(false)
      const message = err instanceof Error ? err.message : String(err)
      // display-option.service.ts's unwrap() has no localized-message table
      // for backend validation codes — it surfaces the raw
      // "DISPLAY_OPTION_NAME_EXISTS: <name>" code instead. Translate this
      // one (the only create-time validation a user can actually trigger
      // from this form — a taken name) rather than showing that raw code.
      const nameExistsMatch = message.match(/^DISPLAY_OPTION_NAME_EXISTS:\s*(.*)$/)
      onError(
        nameExistsMatch
          ? t('simulationV3.displayOptions.dialog.addMode.nameExists', {
              name: nameExistsMatch[1],
              defaultValue: 'A display option named "{{name}}" already exists.',
            })
          : message
      )
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      centered
      width={420}
      footer={null}
      // Bug fix (2026-10-12, reported): hosted from SimulationCanvas's
      // "Manage display options" panel, this Modal is a DESCENDANT of
      // `.simv3-confirm` (z-index 1200, simulationV3.css's "V3 dialog
      // layer"). AntD's own default Modal z-index (~1000) sits below
      // that, so the dialog opened but rendered BEHIND the still-visible
      // Manage panel — invisible and unreachable, reading as "the button
      // does nothing". Same defect simulationV3.css already documents and
      // fixes for `.Gs1OverideModal`/`.affectedsimmodal` (bumped to 1300
      // for the identical reason); matching that value here.
      zIndex={1300}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      styles={{
        header: {
          backgroundColor: dmode ? '#2f353e' : '#fff',
          borderBottom: 'none',
        },
        body: { backgroundColor: dmode ? '#2f353e' : '#fff' },
        content: { backgroundColor: dmode ? '#2f353e' : '#fff' },
      }}
      title={
        <div style={{ fontWeight: 700 }}>
          {t('simulationV3.displayOptions.dialog.addMode.title', {
            defaultValue: 'Add display mode',
          })}
        </div>
      }
      data-testid="add-display-mode-dialog"
    >
      <div className="simv3-doc__field">
        <label className="simv3-doc__field-label">
          {t('simulationV3.displayOptions.dialog.addMode.name', {
            defaultValue: 'Name',
          })}
        </label>
        <input
          type="text"
          className="form-control form-control-sm form__field"
          value={form.name}
          placeholder={t(
            'simulationV3.displayOptions.dialog.addMode.namePlaceholder',
            {
              defaultValue: 'e.g. Tray 40 cm',
            }
          )}
          onChange={(e) => {
            const name = e.target.value
            setForm((f) => ({ ...f, name }))
          }}
          data-testid="add-display-mode-name"
        />
      </div>

      {(['width', 'height', 'depth'] as const).map((dim) => (
        <div className="simv3-doc__field" key={dim}>
          <label className="simv3-doc__field-label">
            {t(`simulationV3.displayOptions.dialog.addMode.${dim}`, {
              defaultValue: dim,
            })}
          </label>
          <input
            type="number"
            min={0}
            className="form-control form-control-sm form__field"
            value={form[dim] ?? ''}
            onChange={(e) => {
              // Read the value NOW, not inside the updater: by the time a
              // queued updater runs, React has restored the controlled
              // input to its old value, so a second edit (40 -> 0) was lost.
              const next = toNumber(e.target.value)
              setForm((f) => ({ ...f, [dim]: next }))
            }}
            data-testid={`add-display-mode-${dim}`}
          />
          {renderFieldError(dim)}
        </div>
      ))}

      <div className="simv3-doc__field">
        <label className="simv3-doc__field-label">
          {t('simulationV3.displayOptions.dialog.addMode.uom', {
            defaultValue: 'UOM',
          })}
        </label>
        <select
          className="form-control form-control-sm form__field simv3-doc__uom"
          value={form.uom ?? ''}
          onChange={(e) => {
            const uom = (e.target.value || null) as Uom
            setForm((f) => ({ ...f, uom }))
          }}
          data-testid="add-display-mode-uom"
        >
          {UOM_OPTIONS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
      </div>

      <div className="simv3-doc__field">
        <label className="simv3-doc__field-label">
          {t('simulationV3.displayOptions.dialog.addMode.capacity', {
            defaultValue: 'Capacity (units per pack)',
          })}
        </label>
        <input
          type="number"
          min={0}
          className="form-control form-control-sm form__field"
          value={form.capacity ?? ''}
          onChange={(e) => {
            const next = toNumber(e.target.value)
            setForm((f) => ({ ...f, capacity: next }))
          }}
          data-testid="add-display-mode-capacity"
        />
        {renderFieldError('capacity')}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          className="simv3-doc__btn simv3-doc__btn--ghost"
          onClick={onClose}
          data-testid="add-display-mode-cancel"
        >
          {t('simulationV3.displayOptions.dialog.addMode.cancel', {
            defaultValue: 'Cancel',
          })}
        </Button>
        <Button
          className="simv3-doc__btn simv3-doc__btn--primary"
          loading={saving}
          disabled={!nameValid || !numbersValid}
          onClick={handleCreate}
          data-testid="add-display-mode-create"
        >
          {t('simulationV3.displayOptions.dialog.addMode.create', {
            defaultValue: 'Create',
          })}
        </Button>
      </div>
    </Modal>
  )
}
